/**
 * Step 2: Body Data - Height, Weight, Age, Gender + BMR Source
 */
import React from 'react';
import { Scale, CheckCircle, Calculator, Activity } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const StepBodyData = ({ prefs, setPrefs, bodyDataFromProfile }) => {
  // Auto-populate prefs from profile data if prefs are empty
  React.useEffect(() => {
    if (bodyDataFromProfile && (!prefs.height_cm || !prefs.weight_kg || !prefs.age || !prefs.gender)) {
      const updates = {};
      if (!prefs.height_cm && bodyDataFromProfile.height_cm) updates.height_cm = bodyDataFromProfile.height_cm;
      if (!prefs.weight_kg && bodyDataFromProfile.weight_kg) updates.weight_kg = bodyDataFromProfile.weight_kg;
      if (!prefs.age && bodyDataFromProfile.age) updates.age = bodyDataFromProfile.age;
      if (!prefs.gender && bodyDataFromProfile.gender) updates.gender = bodyDataFromProfile.gender;
      if (Object.keys(updates).length > 0) {
        setPrefs(prev => ({ ...prev, ...updates }));
      }
    }
  }, [bodyDataFromProfile]);
  // Calculate BMR using Mifflin-St Jeor formula
  const calculateBMR = () => {
    if (!prefs.height_cm || !prefs.weight_kg || !prefs.age || !prefs.gender) return null;
    const bmr = prefs.gender === 'male'
      ? (10 * prefs.weight_kg) + (6.25 * prefs.height_cm) - (5 * prefs.age) + 5
      : (10 * prefs.weight_kg) + (6.25 * prefs.height_cm) - (5 * prefs.age) - 161;
    return Math.round(bmr);
  };

  const calculatedBMR = calculateBMR();
  const displayBMR = prefs.bmr_source === 'manual' && prefs.manual_bmr ? prefs.manual_bmr : calculatedBMR;

  return (
    <div className="space-y-4" data-testid="onboarding-step-body-data">
      <div className="text-center mb-6">
        <Scale className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white">Deine Körperdaten</h3>
        <p className="text-zinc-400 text-sm">Für die Berechnung deines Kalorienbedarfs</p>
      </div>
      
      {bodyDataFromProfile?.height_cm && bodyDataFromProfile?.weight_kg && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
          <p className="text-green-400 text-sm flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Daten aus deinem Profil übernommen
          </p>
        </div>
      )}
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white mb-2 block text-sm">Körpergrösse (cm)</Label>
            <Input
              data-testid="onboarding-height-input"
              type="number"
              min="50"
              max="250"
              value={prefs.height_cm || ''}
              onChange={(e) => {
                const raw = parseFloat(e.target.value);
                const clamped = isNaN(raw) ? null : Math.min(250, Math.max(0, raw));
                setPrefs(prev => ({ ...prev, height_cm: clamped || null }));
              }}
              placeholder="175"
              className="bg-zinc-800 border-zinc-700 text-white h-12"
            />
          </div>
          
          <div>
            <Label className="text-white mb-2 block text-sm">Gewicht (kg)</Label>
            <Input
              data-testid="onboarding-weight-input"
              type="number"
              step="0.1"
              min="20"
              max="500"
              value={prefs.weight_kg || ''}
              onChange={(e) => {
                const raw = parseFloat(e.target.value);
                const clamped = isNaN(raw) ? null : Math.min(500, Math.max(0, raw));
                setPrefs(prev => ({ ...prev, weight_kg: clamped || null }));
              }}
              placeholder="75.5"
              className="bg-zinc-800 border-zinc-700 text-white h-12"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white mb-2 block text-sm">Alter</Label>
            <Input
              data-testid="onboarding-age-input"
              type="number"
              min="10"
              max="110"
              value={prefs.age || ''}
              onChange={(e) => {
                const raw = parseInt(e.target.value);
                const clamped = isNaN(raw) ? null : Math.min(110, Math.max(0, raw));
                setPrefs(prev => ({ ...prev, age: clamped || null }));
              }}
              placeholder="30"
              className="bg-zinc-800 border-zinc-700 text-white h-12"
            />
          </div>
          
          <div>
            <Label className="text-white mb-2 block text-sm">Geschlecht</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPrefs(prev => ({ ...prev, gender: 'male' }))}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  prefs.gender === 'male'
                    ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                Mann
              </button>
              <button
                onClick={() => setPrefs(prev => ({ ...prev, gender: 'female' }))}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  prefs.gender === 'female'
                    ? 'border-pink-500 bg-pink-500/20 text-pink-400'
                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                Frau
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* BMR Source Selection - NEW */}
      {prefs.height_cm > 0 && prefs.weight_kg > 0 && prefs.age > 0 && prefs.gender && (
        <div className="mt-6 pt-4 border-t border-zinc-700">
          <Label className="text-white mb-3 block text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-orange-500" />
            Grundumsatz (BMR)
          </Label>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              data-testid="bmr-source-calculated"
              onClick={() => setPrefs(prev => ({ ...prev, bmr_source: 'calculated', manual_bmr: null }))}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                prefs.bmr_source === 'calculated' || !prefs.bmr_source
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Calculator className="w-4 h-4 text-green-500" />
                <span className="font-medium text-white text-sm">Automatisch berechnen</span>
              </div>
              <p className="text-xs text-zinc-400">Basierend auf deinen Körperdaten</p>
              {calculatedBMR && prefs.bmr_source !== 'manual' && (
                <p className="text-lg font-bold text-green-400 mt-2">{calculatedBMR} kcal</p>
              )}
            </button>
            
            <button
              data-testid="bmr-source-manual"
              onClick={() => setPrefs(prev => ({ ...prev, bmr_source: 'manual' }))}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                prefs.bmr_source === 'manual'
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Scale className="w-4 h-4 text-orange-500" />
                <span className="font-medium text-white text-sm">Manuell eingeben</span>
              </div>
              <p className="text-xs text-zinc-400">Von Diagnostikmessung (InBody etc.)</p>
            </button>
          </div>
          
          {/* Manual BMR Input */}
          {prefs.bmr_source === 'manual' && (
            <div className="space-y-3 p-4 bg-orange-500/5 border border-orange-500/30 rounded-lg">
              <div>
                <Label className="text-zinc-300 mb-2 block text-xs">Gemessener Grundumsatz (kcal)</Label>
                <Input
                  data-testid="manual-bmr-input"
                  type="number"
                  value={prefs.manual_bmr != null ? prefs.manual_bmr : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPrefs(prev => ({ ...prev, manual_bmr: val ? parseInt(val, 10) : null }));
                  }}
                  placeholder="z.B. 1650"
                  className="bg-zinc-800 border-zinc-700 text-white h-12 text-lg font-bold"
                />
              </div>
              <div>
                <Label className="text-zinc-300 mb-2 block text-xs">Messgerät (optional)</Label>
                <Input
                  type="text"
                  value={prefs.bmr_measurement_device || ''}
                  onChange={(e) => setPrefs(prev => ({ ...prev, bmr_measurement_device: e.target.value }))}
                  placeholder="z.B. InBody 770, Tanita MC-780"
                  className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm"
                />
              </div>
              {prefs.manual_bmr && calculatedBMR && (
                <div className="p-2 bg-zinc-800/50 rounded text-xs">
                  <span className="text-zinc-400">Vergleich: </span>
                  <span className={prefs.manual_bmr > calculatedBMR ? 'text-green-400' : 'text-red-400'}>
                    {prefs.manual_bmr > calculatedBMR ? '+' : ''}{prefs.manual_bmr - calculatedBMR} kcal 
                  </span>
                  <span className="text-zinc-500"> vs. berechnet ({calculatedBMR} kcal)</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* BMI Display */}
      {prefs.height_cm > 0 && prefs.weight_kg > 0 && (
        <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-zinc-400 text-sm mb-1">Dein BMI</p>
              <p className="text-2xl font-bold text-white">
                {(prefs.weight_kg / Math.pow(prefs.height_cm / 100, 2)).toFixed(1)}
              </p>
            </div>
            {displayBMR && (
              <div className="text-right">
                <p className="text-zinc-400 text-sm mb-1">
                  Grundumsatz {prefs.bmr_source === 'manual' ? '(gemessen)' : '(berechnet)'}
                </p>
                <p className={`text-2xl font-bold ${prefs.bmr_source === 'manual' ? 'text-orange-400' : 'text-white'}`}>
                  {displayBMR} kcal
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepBodyData;
